import type { PublicCourse } from '@/types/course'

export type StageId =
  | 'become-ba'
  | 'systems'
  | 'apply-ai'
  | 'build-product'
  | 'cert'

export interface StageCardConfig {
  id: StageId
  icon: string
  title: string
  description: string
  pathTitle: string
  /** Course codes in path order */
  courseCodes: string[]
  /** Ordered “học những gì” steps (not course names) */
  pathLearnSteps: string[]
  /** Knowledge topic chips */
  skillsToLearn: string[]
}

export interface SkillConfig {
  name: string
  level: number
  icon: string
  learn: string[]
  courseCodes: string[]
  detail: string
}

export interface CompareCourse {
  id: string
  name: string
  courseCodes: string[]
}

export interface CompareCriterion {
  id: string
  title: string
  subtitle?: string
  /** CompareCourse.id values that have this capability */
  courseIds: string[]
}

export const STAGE_CARDS: StageCardConfig[] = [
  {
    id: 'become-ba',
    icon: 'school',
    title: 'Bắt đầu sự nghiệp BA',
    description: 'Khóa BA thực chiến thời kỳ AI — vào nghề qua dự án thực tế và tư duy AI-first.',
    pathTitle: 'Lộ trình · Bắt đầu sự nghiệp BA',
    courseCodes: ['BAAIONL'],
    pathLearnSteps: [
      'Nắm tư duy BA và cách làm việc với stakeholder',
      'Viết BRD, User Story, vẽ BPMN đúng ngữ cảnh',
      'Phân tích yêu cầu và làm việc với dự án thực tế',
      'Áp dụng AI cơ bản vào công việc BA hàng ngày',
    ],
    skillsToLearn: [
      'BA mindset',
      'BRD / User Story',
      'BPMN',
      'Elicitation',
      'AI cơ bản cho BA',
    ],
  },
  {
    id: 'systems',
    icon: 'database',
    title: 'Hiểu sâu hệ thống',
    description: 'Nắm API, Database và AI Coding để đọc hệ thống rõ và tự dựng luồng kỹ thuật.',
    pathTitle: 'Lộ trình · Hiểu sâu hệ thống',
    courseCodes: ['Database', 'API', 'AICODING'],
    pathLearnSteps: [
      'Hiểu mô hình dữ liệu: ERD, data model, SQL cơ bản',
      'Đọc tài liệu API (REST, Swagger) và test bằng Postman',
      'Nắm Auth / JSON để trao đổi rõ với Dev',
      'Dùng AI Coding để dựng và kiểm chứng phần kỹ thuật',
    ],
    skillsToLearn: [
      'ERD',
      'SQL cơ bản',
      'Data model',
      'REST / Swagger',
      'Auth / JSON / Postman',
      'AI Coding',
    ],
  },
  {
    id: 'apply-ai',
    icon: 'smart_toy',
    title: 'Tăng tốc bằng AI',
    description: 'AI4BA và AI Coding để tăng tốc tài liệu, workflow và sản phẩm demo.',
    pathTitle: 'Lộ trình · Tăng tốc bằng AI',
    courseCodes: ['AI4BA', 'AICODING'],
    pathLearnSteps: [
      'Áp dụng AI vào tài liệu và diagram BA',
      'Tạo skill / agent cho các task lặp lại',
      'Tăng tốc quy trình BA với AI trong công việc hàng ngày',
      'Dùng AI Coding để dựng Website / App demo',
    ],
    skillsToLearn: [
      'Skills / Agents',
      'Diagram AI',
      'Prototype AI',
      'AI Coding',
    ],
  },
  {
    id: 'build-product',
    icon: 'devices',
    title: 'Tự xây dựng MVP',
    description: 'Từng bước dựng website/app demo mà không phải chờ Dev làm trước.',
    pathTitle: 'Lộ trình · Tạo sản phẩm',
    courseCodes: ['BAAIONL', 'AICODING'],
    pathLearnSteps: [
      'Có nền tảng BA thực chiến để định nghĩa đúng sản phẩm',
      'Dùng AI Coding để dựng Website / App',
      'Deploy bản demo và iterate theo feedback',
    ],
    skillsToLearn: [
      'Nền tảng BA thực chiến',
      'AI Coding',
      'Website / App',
      'Deploy cơ bản',
    ],
  },
  {
    id: 'cert',
    icon: 'military_tech',
    title: 'Chinh phục chứng chỉ BA',
    description:
      'Ôn thi chứng chỉ CCBA/CBAP — bộ đề giống đề thi thật, không cần trả thêm chi phí mua đề ôn bên ngoài.',
    pathTitle: 'Lộ trình · Chinh phục chứng chỉ BA',
    courseCodes: ['Ccba_cbap'],
    pathLearnSteps: [
      'Ôn khung BABOK và các knowledge area với tài liệu đọc đầy đủ',
      'Học qua video giảng dạy chi tiết theo từng chủ đề thi',
      'Luyện kho ngân hàng câu hỏi phong phú — bộ đề giống đề thi thật',
      'Không cần trả thêm chi phí mua bộ đề ôn thi bên ngoài',
      'Chọn đúng cấp thi CCBA / CBAP và xây chiến lược làm bài',
    ],
    skillsToLearn: [
      'BABOK',
      'CCBA / CBAP',
      'Bộ đề giống đề thi thật',
      'Ngân hàng câu hỏi',
      'Video + tài liệu đầy đủ',
    ],
  },
]

/** Map stage id → audience filter for /courses */
export const STAGE_AUDIENCE_MAP: Record<StageId, string> = {
  'become-ba': 'beginner',
  systems: 'tech',
  'apply-ai': 'ai',
  'build-product': 'ai',
  cert: 'cert',
}

export const SKILL_MAP: SkillConfig[] = [
  {
    name: 'Business Analysis',
    level: 100,
    icon: 'analytics',
    detail: 'Tư duy BA, elicitation, tài liệu hóa BRD/SRS/User Story, làm việc với stakeholder.',
    learn: ['BA mindset', 'BRD/SRS', 'User Story', 'BPMN'],
    courseCodes: ['BAAIONL'],
  },
  {
    name: 'AI',
    level: 85,
    icon: 'bolt',
    detail: 'Prompt → Skill → Agent → Workflow; dùng AI trong diagram, prototype, tài liệu BA.',
    learn: ['Skills/Agents', 'Diagram AI', 'Prototype', 'Doc sync'],
    courseCodes: ['AI4BA'],
  },
  {
    name: 'API',
    level: 55,
    icon: 'api',
    detail: 'REST, Swagger, Auth, JSON, Postman — BA đọc và test API cùng Dev.',
    learn: ['REST', 'Swagger', 'Auth', 'JSON', 'Postman'],
    courseCodes: ['API'],
  },
  {
    name: 'Database',
    level: 55,
    icon: 'database',
    detail: 'ERD, SQL cơ bản, data model — BA hiểu dữ liệu phía sau màn hình.',
    learn: ['ERD', 'SQL cơ bản', 'Data model'],
    courseCodes: ['Database'],
  },
  {
    name: 'Coding',
    level: 62,
    icon: 'code',
    detail: 'AI Coding để build website/app và deploy — BA tự tạo MVP.',
    learn: ['AI Coding', 'Website/App', 'Deploy'],
    courseCodes: ['AICODING'],
  },
  {
    name: 'Certification',
    level: 40,
    icon: 'verified',
    detail:
      'Ôn thi chứng chỉ CCBA/CBAP với bộ đề giống đề thi thật, ngân hàng câu hỏi phong phú, video giảng chi tiết và tài liệu đầy đủ — không cần mua thêm đề ngoài.',
    learn: ['CCBA', 'CBAP', 'Bộ đề giống đề thi thật'],
    courseCodes: ['Ccba_cbap'],
  },
]

export const COMPARE_COURSES: CompareCourse[] = [
  {
    id: 'ba',
    name: 'BA thực chiến thời kỳ AI',
    courseCodes: ['BAAIONL'],
  },
  {
    id: 'api',
    name: 'API for BA',
    courseCodes: ['API'],
  },
  {
    id: 'database',
    name: 'Database for BA',
    courseCodes: ['Database'],
  },
  {
    id: 'ai4ba',
    name: 'AI4BA',
    courseCodes: ['AI4BA'],
  },
  {
    id: 'ai-coding',
    name: 'AI Coding',
    courseCodes: ['AICODING'],
  },
  {
    id: 'cert',
    name: 'Ôn thi chứng chỉ CCBA/CBAP',
    courseCodes: ['Ccba_cbap'],
  },
]

export const COMPARE_CRITERIA: CompareCriterion[] = [
  {
    id: 'ba-foundation',
    title: 'Nắm vững nền tảng BA',
    courseIds: ['ba'],
  },
  {
    id: 'tech-knowledge',
    title: 'Hiểu kiến thức kỹ thuật',
    subtitle: 'Trao đổi dễ với Dev · Viết yêu cầu chính xác hơn',
    courseIds: ['api', 'database', 'ai-coding'],
  },
  {
    id: 'ai-speed',
    title: 'Tăng tốc công việc bằng AI',
    courseIds: ['ba', 'ai4ba'],
  },
  {
    id: 'automation-build',
    title: 'Tự động hóa & dựng sản phẩm',
    courseIds: ['ba', 'ai4ba', 'ai-coding'],
  },
  {
    id: 'systemize-ba',
    title: 'Hệ thống hóa kiến thức BA chuẩn nghề',
    courseIds: ['ba', 'cert'],
  },
  {
    id: 'intl-cert',
    title: 'Chứng minh năng lực quốc tế (CCBA/CBAP)',
    courseIds: ['cert'],
  },
]

export function normalizeCourseCode(code?: string | null): string {
  return (code || '').trim().toUpperCase()
}

export function findCoursesByCodes(
  courses: PublicCourse[],
  codes: string[]
): PublicCourse[] {
  const byCode = new Map<string, PublicCourse[]>()
  for (const c of courses) {
    const key = normalizeCourseCode(c.code)
    const list = byCode.get(key) || []
    list.push(c)
    byCode.set(key, list)
  }

  const result: PublicCourse[] = []
  const seen = new Set<number>()
  for (const raw of codes) {
    const key = normalizeCourseCode(raw)
    const matches = byCode.get(key) || []
    for (const m of matches) {
      if (!seen.has(m.id)) {
        seen.add(m.id)
        result.push(m)
      }
    }
  }
  return result
}

/** Resolve a course by exact code first, then by name keywords. */
export function findCourseByCodeOrKeywords(
  courses: PublicCourse[],
  code: string,
  keywords: string[] = []
): PublicCourse | null {
  const byCode = findCoursesByCodes(courses, [code])[0]
  if (byCode) return byCode

  const needles = keywords
    .map((k) => k.trim().toLowerCase())
    .filter(Boolean)
  if (needles.length === 0) return null

  return (
    courses.find((course) => {
      const haystack = `${course.name} ${course.code}`.toLowerCase()
      return needles.every((needle) => haystack.includes(needle))
    }) ||
    courses.find((course) => {
      const haystack = `${course.name} ${course.code}`.toLowerCase()
      return needles.some((needle) => haystack.includes(needle))
    }) ||
    null
  )
}

export function courseLink(course: PublicCourse): string {
  return `/courses/${course.hashId || course.id}`
}

/** Fallback list page when course is not resolved yet. */
export function courseSearchFallback(label: string): string {
  return `/courses?search=${encodeURIComponent(label)}`
}
